// Layout-engine ports of the code-drawn form generators (Cease & Desist,
// Vehicle Bill of Sale, ...), so a business can start a template from any of
// them in the editor and the generator can render that design. Each entry
// carries the starter layout, the formData -> token mapping the layout
// renders from, the sample data the editor previews with, and the token
// groups / show-when presets the editor offers. layoutEngine registers them;
// this module must not import layoutEngine.
import { LEGAL_FLOW_DOC_TYPES } from "./formDocLegalFlow";
import { UTILITY_DOC_TYPES } from "./formDocUtility";

const fmtLongDate = (s) => {
  if (!s) return "";
  const str = String(s);
  const d = new Date(/^\d{4}-\d{2}-\d{2}$/.test(str) ? `${str}T00:00:00` : str);
  return isNaN(d.getTime()) ? str : d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
};
const money = (v) => (parseFloat(v) || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });
const flag = (v) => (v ? "true" : "");
const todayIso = () => new Date().toISOString().slice(0, 10);

// Text element whose first baseline sits at `baseline` (the generators place
// text by baseline; the engine draws the baseline 0.85 x font size below y).
const T = (id, x, baseline, w, content, fontSize, extra = {}) =>
  ({ id, type: "text", x, y: +(baseline - 0.85 * fontSize).toFixed(2), w, content, fontSize, ...extra });

// ── Cease and Desist (the generator's "Professional" style, Times) ──────────
// The letter flows: every block follows the one before it (`after`), a block
// that has nothing to say takes no space, and the gaps are the generator's
// own (its 15pt line pitch, the 10pt between paragraphs, and so on).
const CD_PRIMARY = "#1a2647";
const CD_ACCENT = "#b81c1c";
const CD_TEXT = "#1a1a1a";
const CD_GRAY = "#595959";
const CD_L = 60;
const CD_W = 492;
const cdText = (id, content, fontSize, pitch, extra = {}) =>
  ({ id, type: "text", x: CD_L, w: CD_W, wrap: true, font: "times", content, fontSize, lineHeight: +(pitch / fontSize).toFixed(4), color: CD_TEXT, ...extra });

export const DEFAULT_CEASE_AND_DESIST_LAYOUT = {
  page: { width: 612, height: 792 },
  meta: { baseDocType: "cease-and-desist", flow: "baseline", flowMargins: { top: 60, bottom: 100 } },
  elements: [
    { id: "cd-logo", type: "image", x: CD_L, y: 60, w: 200, h: 48, fit: "contain", src: "{logoDataUrl}", showIf: "hasLogo", spaceAfter: 12 },
    cdText("cd-sender", "{senderName}", 14, 16, { after: "cd-logo", bold: true, color: CD_PRIMARY, showIf: "hasSenderName" }),
    cdText("cd-saddr", "{senderAddress}", 10, 12, { after: "cd-sender", color: CD_GRAY, showIf: "hasSenderAddress" }),
    cdText("cd-scsz", "{senderCityStateZip}", 10, 12, { after: "cd-saddr", color: CD_GRAY, showIf: "hasSenderCityStateZip" }),
    cdText("cd-sphone", "{senderPhone}", 10, 12, { after: "cd-scsz", color: CD_GRAY, showIf: "hasSenderPhone" }),
    cdText("cd-semail", "{senderEmail}", 10, 12, { after: "cd-sphone", color: CD_GRAY, showIf: "hasSenderEmail" }),
    { id: "cd-rule", type: "line", after: "cd-semail", offset: 6, x: CD_L, w: CD_W, h: 0, color: CD_ACCENT, lineWidth: 1.5, spaceAfter: 24 },
    cdText("cd-date", "{letterDate}", 11, 15, { after: "cd-rule", spaceAfter: 12 }),
    cdText("cd-rname", "{recipientName}", 11, 15, { after: "cd-date", bold: true, showIf: "hasRecipientName" }),
    cdText("cd-rcompany", "{recipientCompany}", 11, 15, { after: "cd-rname", showIf: "hasRecipientCompany" }),
    cdText("cd-raddr", "{recipientAddress}", 11, 15, { after: "cd-rcompany", showIf: "hasRecipientAddress" }),
    cdText("cd-rcsz", "{recipientCityStateZip}", 11, 15, { after: "cd-raddr", showIf: "hasRecipientCityStateZip" }),
    // the gap under the recipient block applies however many of its lines show
    { id: "cd-gap1", type: "rect", after: "cd-rcsz", x: CD_L, w: CD_W, h: 0, fill: "none", stroke: "none", spaceAfter: 14 },
    cdText("cd-delivery", "{deliveryMethod}", 9.5, 15, { after: "cd-gap1", bold: true, color: "#666666", showIf: "hasDeliveryMethod", spaceAfter: 10 }),
    cdText("cd-subject", "{subject}", 12, 15, { after: "cd-delivery", bold: true, color: CD_ACCENT, spaceAfter: 14, keep: 40 }),
    cdText("cd-salutation", "{salutation}", 11, 15, { after: "cd-subject", spaceAfter: 12 }),
    cdText("cd-body1", "{bodyParagraph1}", 11, 15, { after: "cd-salutation", spaceAfter: 10 }),
    cdText("cd-body2", "{bodyParagraph2}", 11, 15, { after: "cd-body1", spaceAfter: 10 }),
    cdText("cd-body3", "{bodyParagraph3}", 11, 15, { after: "cd-body2", spaceAfter: 10 }),
    cdText("cd-incident", "{incidentStatement}", 11, 15, { after: "cd-body3", showIf: "hasIncident", spaceAfter: 10 }),
    cdText("cd-additional", "{additionalDetails}", 11, 15, { after: "cd-incident", showIf: "hasAdditionalDetails", spaceAfter: 10 }),
    cdText("cd-demand", "{demandText}", 11, 15, { after: "cd-additional", spaceAfter: 10, keep: 60 }),
    cdText("cd-consequence", "{consequenceText}", 11, 15, { after: "cd-demand", spaceAfter: 10, keep: 60 }),
    cdText("cd-reservation", "{reservationText}", 10, 15, { after: "cd-consequence", italic: true, color: "#4d4d4d", spaceAfter: 22, keep: 50 }),
    // the signature block stays together (the generator keeps 110pt for it)
    cdText("cd-sincerely", "Sincerely,", 11, 15, { after: "cd-reservation", spaceAfter: 24, keep: 110 }),
    { id: "cd-sigimg", type: "image", after: "cd-sincerely", offset: -10, x: CD_L, w: 220, h: 48, fit: "contain", src: "{signatureImage}", showIf: "hasSignatureImage", spaceAfter: 22 },
    cdText("cd-sigtyped", "{signatureName}", 22, 22, { after: "cd-sigimg", font: "yellowtail", color: CD_PRIMARY, showIf: "hasSignatureName" }),
    cdText("cd-signame", "{senderName}", 11, 13, { after: "cd-sigtyped", bold: true, showIf: "hasSenderName" }),
    cdText("cd-sigtitle", "{senderTitle}", 10, 13, { after: "cd-signame", color: CD_GRAY, showIf: "hasSenderTitle" }),
  ],
};

// Body copy per type of conduct, word for word the generator's
const CD_BODIES = {
  harassment: (d) => [
    `This letter serves as formal notice to immediately CEASE AND DESIST all harassing conduct directed toward ${d.senderName || "the sender"}.`,
    `It has come to my attention that you have engaged in the following conduct: ${d.description || "harassing behavior"}. This conduct is unwelcome, has caused substantial distress, and may constitute harassment under applicable state and federal law.`,
    `You are hereby directed to immediately stop all such conduct, including but not limited to contacting, following, monitoring, or communicating with me by any means, whether directly or through third parties.`,
  ],
  defamation: (d) => [
    `This letter serves as formal notice to immediately CEASE AND DESIST the publication of false and defamatory statements concerning ${d.senderName || "the sender"}.`,
    `Specifically, you have made the following false statements: ${d.description || "defamatory statements"}. These statements are false, were published to third parties, and have caused damage to my reputation, business, and standing in the community.`,
    `You are hereby directed to immediately cease making such statements, to retract all prior statements, and to remove any such content published online or in any other medium.`,
  ],
  "ip-infringement": (d) => [
    `This letter serves as formal notice to immediately CEASE AND DESIST all unauthorized use of intellectual property owned by ${d.senderName || "the sender"}.`,
    `It has come to my attention that you are using the following protected material without authorization: ${d.description || "protected intellectual property"}. I am the rightful owner of this material and have never granted you a license or permission to use it.`,
    `You are hereby directed to immediately stop all use, reproduction, distribution, and display of the material, and to remove it from all platforms, products, and marketing materials under your control.`,
  ],
  "debt-collection": (d) => [
    `This letter serves as formal notice to immediately CEASE AND DESIST all further communication regarding the alleged debt referenced below.`,
    `Pursuant to the Fair Debt Collection Practices Act (FDCPA), 15 U.S.C. § 1692c(c), you are required to cease all communication with me regarding this alleged debt upon receipt of this written request. The matter concerns: ${d.description || "the alleged debt"}.`,
    `You may only contact me hereafter to confirm that further communication has ceased or to notify me of a specific legal remedy you intend to pursue. Any further contact in violation of this notice may subject you to statutory damages.`,
  ],
  "breach-of-contract": (d) => [
    `This letter serves as formal notice to immediately CEASE AND DESIST conduct that constitutes a breach of the agreement between us.`,
    `You are in breach of our agreement in the following respects: ${d.description || "the breaching conduct"}. This conduct is a material violation of the terms to which you agreed.`,
    `You are hereby directed to immediately cure this breach and to cease all conduct that violates the terms of our agreement.`,
  ],
  other: (d) => [
    `This letter serves as formal notice to immediately CEASE AND DESIST the conduct described below.`,
    `The conduct at issue is as follows: ${d.description || "the conduct at issue"}. This conduct is unlawful and/or violates my legal rights.`,
    `You are hereby directed to immediately stop this conduct and to refrain from engaging in it in the future.`,
  ],
};
const CD_DELIVERY = {
  "certified-mail": "VIA CERTIFIED MAIL, RETURN RECEIPT REQUESTED",
  email: "VIA ELECTRONIC MAIL",
  both: "VIA CERTIFIED MAIL AND ELECTRONIC MAIL",
  "hand-delivery": "VIA HAND DELIVERY",
};
const CD_RESERVATION = "This letter is written without prejudice to any of my rights and remedies, all of which are expressly reserved. Nothing contained herein shall be construed as a waiver of any right or remedy available to me at law or in equity.";
const CD_DEFAULT_CONSEQUENCE = "Should you fail to comply with this demand, I am prepared to pursue all available legal remedies, which may include filing a civil action seeking injunctive relief, monetary damages, and recovery of attorneys' fees and costs to the fullest extent permitted by law.";

// "City, ST 12345" the way the generator joins it; "" when there is nothing
const cityStateZip = (city, state, zip) => {
  const s = [city, state].filter(Boolean).join(", ") + (zip ? ` ${zip}` : "");
  const t = s.trim();
  return t && t !== "," ? t : "";
};

export function buildCeaseAndDesistContext(td) {
  const f = (td && td.formData) || {};
  const body = (CD_BODIES[f.violationType] || CD_BODIES.other)(f);
  const incident = [];
  if (f.incidentDate) incident.push(`on or about ${fmtLongDate(f.incidentDate)}`);
  if (f.incidentLocation) incident.push(`at ${f.incidentLocation}`);
  const days = parseInt(f.complianceDays, 10) || 10;
  const additional = String(f.additionalDetails || "").trim();
  const signatureImage = f.signatureImage || "";
  const signatureName = String(f.signatureName || "").trim() || f.senderName || "";
  const senderCsz = cityStateZip(f.senderCity, f.senderState, f.senderZip);
  const recipientCsz = cityStateZip(f.recipientCity, f.recipientState, f.recipientZip);
  const subject = String(f.subject || "").trim() || "CEASE AND DESIST DEMAND";
  return {
    senderName: f.senderName || "", hasSenderName: flag(f.senderName),
    senderTitle: f.senderTitle || "", hasSenderTitle: flag(f.senderTitle),
    senderAddress: f.senderAddress || "", hasSenderAddress: flag(String(f.senderAddress || "").trim()),
    senderCityStateZip: senderCsz, hasSenderCityStateZip: flag(senderCsz),
    senderPhone: f.senderPhone || "", hasSenderPhone: flag(String(f.senderPhone || "").trim()),
    senderEmail: f.senderEmail || "", hasSenderEmail: flag(String(f.senderEmail || "").trim()),
    logoDataUrl: f.senderLogo || "", hasLogo: flag(f.senderLogo),
    letterDate: fmtLongDate(f.letterDate) || fmtLongDate(todayIso()),
    recipientName: f.recipientName || "", hasRecipientName: flag(f.recipientName),
    recipientCompany: f.recipientCompany || "", hasRecipientCompany: flag(String(f.recipientCompany || "").trim()),
    recipientAddress: f.recipientAddress || "", hasRecipientAddress: flag(String(f.recipientAddress || "").trim()),
    recipientCityStateZip: recipientCsz, hasRecipientCityStateZip: flag(recipientCsz),
    deliveryMethod: CD_DELIVERY[f.deliveryMethod] || "", hasDeliveryMethod: flag(CD_DELIVERY[f.deliveryMethod]),
    subject: `RE: ${subject.toUpperCase()}`,
    salutation: f.recipientName ? `Dear ${f.recipientName}:` : "To Whom It May Concern:",
    violationType: f.violationType || "other",
    description: f.description || "",
    bodyParagraph1: body[0], bodyParagraph2: body[1], bodyParagraph3: body[2],
    incidentStatement: incident.length ? `The conduct referenced above occurred ${incident.join(" ")}.` : "",
    hasIncident: flag(incident.length),
    incidentDate: fmtLongDate(f.incidentDate), incidentLocation: f.incidentLocation || "",
    additionalDetails: additional, hasAdditionalDetails: flag(additional),
    complianceDays: String(days),
    demandText: `You are required to comply with this demand within ${days} days of receipt of this letter. Please confirm in writing that you have complied and that you will refrain from such conduct in the future.`,
    consequenceText: String(f.legalAction || "").trim() || CD_DEFAULT_CONSEQUENCE,
    reservationText: CD_RESERVATION,
    signatureImage, hasSignatureImage: flag(signatureImage),
    signatureName, hasSignatureName: flag(!signatureImage && signatureName),
    todayDate: fmtLongDate(todayIso()),
  };
}

export const CEASE_AND_DESIST_SAMPLE = {
  formData: {
    template: "professional", primaryColor: "#1a1a1a", accentColor: "#b71c1c",
    senderName: "John Smith", senderAddress: "123 Main Street",
    senderCity: "New York", senderState: "NY", senderZip: "10001",
    senderPhone: "(555) 123-4567", senderEmail: "john@example.com",
    recipientName: "Jane Doe", recipientCompany: "Acme Collections LLC",
    recipientAddress: "456 Business Ave", recipientCity: "New York", recipientState: "NY", recipientZip: "10002",
    letterDate: "2026-09-01", violationType: "harassment",
    description: "Repeated unwanted phone calls and messages at all hours despite multiple requests to stop contacting me.",
    complianceDays: "10", deliveryMethod: "certified-mail", signatureName: "John Smith",
  },
};

export const CEASE_AND_DESIST_TOKEN_GROUPS = [
  { group: "Sender", tokens: [
    ["{senderName}", "Sender name"], ["{senderTitle}", "Sender title"], ["{senderAddress}", "Sender address"],
    ["{senderCityStateZip}", "Sender city/state/zip"], ["{senderPhone}", "Sender phone"], ["{senderEmail}", "Sender email"],
  ] },
  { group: "Recipient", tokens: [
    ["{recipientName}", "Recipient name"], ["{recipientCompany}", "Recipient company"],
    ["{recipientAddress}", "Recipient address"], ["{recipientCityStateZip}", "Recipient city/state/zip"],
  ] },
  { group: "Letter", tokens: [
    ["{letterDate}", "Letter date"], ["{deliveryMethod}", "Delivery method line"], ["{subject}", "Subject (RE: ...)"],
    ["{salutation}", "Salutation"], ["{bodyParagraph1}", "Body paragraph 1"], ["{bodyParagraph2}", "Body paragraph 2"],
    ["{bodyParagraph3}", "Body paragraph 3"], ["{description}", "Conduct described"], ["{incidentStatement}", "Incident sentence"],
    ["{incidentDate}", "Incident date"], ["{incidentLocation}", "Incident location"], ["{additionalDetails}", "Additional details"],
    ["{complianceDays}", "Days to comply"], ["{demandText}", "Demand paragraph"], ["{consequenceText}", "Consequences paragraph"],
    ["{reservationText}", "Reservation of rights"],
  ] },
  { group: "Signature", tokens: [
    ["{signatureName}", "Typed signature name"], ["{todayDate}", "Today's date"],
  ] },
];

export const CEASE_AND_DESIST_SHOW_IF = [
  ["", "Always"],
  ["hasLogo", "Has letterhead logo"],
  ["hasSenderTitle", "Has sender title"],
  ["hasRecipientCompany", "Has recipient company"],
  ["hasDeliveryMethod", "Has delivery method"],
  ["hasIncident", "Has incident date or location"],
  ["hasAdditionalDetails", "Has additional details"],
  ["hasSignatureImage", "Signature is an image"],
  ["hasSignatureName", "Signature is typed"],
  ["violationType=harassment", "Conduct: harassment"],
  ["violationType=defamation", "Conduct: defamation"],
  ["violationType=ip-infringement", "Conduct: IP infringement"],
  ["violationType=debt-collection", "Conduct: debt collection"],
  ["violationType=breach-of-contract", "Conduct: breach of contract"],
  ["violationType=other", "Conduct: other"],
];

// ── Vehicle Bill of Sale (the generator's "Classic" style, Helvetica) ───────
// The page is fixed except below the condition block: a warranty line may
// appear there, and everything under it moves down with it. The notary
// acknowledgment, when asked for, is its own page, as the generator does once
// the first page is full.
const VB_PRIMARY = "#1a1a4d";
const VB_TEXT = "#1a1a1a";
const VB_BORDER = "#4d4d4d";
const VB_L = 50;
const VB_W = 512;
const vbHeader = (id, top, text) => [
  { id: `${id}-bar`, type: "rect", x: VB_L, y: top - 2, w: VB_W, h: 18, fill: VB_PRIMARY, stroke: "none" },
  T(`${id}-t`, VB_L + 8, top + 12, 400, text, 10, { bold: true, color: "#ffffff" }),
];
const vbLabel = (id, x, baseline, text, w = 120) => T(id, x, baseline, w, text, 9, { bold: true, color: VB_TEXT });
const vbValue = (id, x, baseline, content, w = 200, extra = {}) => T(id, x, baseline, w, content, 10, { color: VB_TEXT, ...extra });
const vbCheck = (id, baseline, showIf, label) => [
  { id: `${id}-box`, type: "rect", x: VB_L + 10, y: baseline - 2, w: 10, h: 10, fill: "none", stroke: "#000000", lineWidth: 1 },
  T(`${id}-x`, VB_L + 12, baseline + 6, 12, "X", 10, { bold: true, color: VB_TEXT, showIf }),
  T(`${id}-label`, VB_L + 28, baseline + 5, 460, label, 9, { color: VB_TEXT }),
];
// A text chained by baseline: `offset` is where its first baseline lands
// below the element before it.
const vbFlow = (id, x, w, content, fontSize, pitch, extra = {}) =>
  ({ id, type: "text", x, w, wrap: true, content, fontSize, lineHeight: +(pitch / fontSize).toFixed(4), color: VB_TEXT, ...extra });
const VB_SIG_RIGHT = VB_L + 236 + 40; // the buyer column
const vbSigColumn = (side, x) => [
  T(`vb-sig-${side}-label`, x, 0, 200, side === "s" ? "SELLER:" : "BUYER:", 9, { bold: true, color: VB_TEXT, after: "vb-sig-h-t", offset: 17, lineHeight: 1, spaceAfter: 21 }),
  { id: `vb-sig-${side}-line1`, type: "line", after: `vb-sig-${side}-label`, x, w: 236, h: 0, color: VB_BORDER, lineWidth: 1, spaceAfter: 12 },
  T(`vb-sig-${side}-cap1`, x, 0, 200, "Signature", 8, { color: VB_TEXT, after: `vb-sig-${side}-line1`, lineHeight: 1, spaceAfter: 12 }),
  T(`vb-sig-${side}-name`, x, 0, 236, side === "s" ? "{sellerPrintName}" : "{buyerPrintName}", 10, { color: VB_TEXT, after: `vb-sig-${side}-cap1`, offset: -8, lineHeight: 1 }),
  { id: `vb-sig-${side}-line2`, type: "line", after: `vb-sig-${side}-cap1`, x, w: 236, h: 0, color: VB_BORDER, lineWidth: 1, spaceAfter: 12 },
  T(`vb-sig-${side}-cap2`, x, 0, 200, "Print Name", 8, { color: VB_TEXT, after: `vb-sig-${side}-line2`, lineHeight: 1, spaceAfter: 12 }),
  { id: `vb-sig-${side}-line3`, type: "line", after: `vb-sig-${side}-cap2`, x, w: 236, h: 0, color: VB_BORDER, lineWidth: 1, spaceAfter: 12 },
  T(`vb-sig-${side}-cap3`, x, 0, 200, "Date", 8, { color: VB_TEXT, after: `vb-sig-${side}-line3`, lineHeight: 1 }),
].map((el) => (el.type === "text" ? { ...el, y: undefined } : el));

export const DEFAULT_VEHICLE_BILL_OF_SALE_LAYOUT = {
  page: { width: 612, height: 792 },
  meta: { baseDocType: "vehicle-bill-of-sale", flow: "baseline", flowMargins: { top: 42, bottom: 16 } },
  elements: [
    T("vb-title", 0, 35, 612, "VEHICLE BILL OF SALE", 20, { bold: true, color: VB_PRIMARY, align: "center" }),
    { id: "vb-border", type: "rect", x: 40, y: 85, w: 532, h: 677, fill: "none", stroke: VB_PRIMARY, lineWidth: 2 },
    T("vb-state", VB_L, 65, 190, "State of {state}", 10, { color: VB_TEXT }),
    T("vb-county", VB_L + 200, 65, 150, "County of {county}", 10, { color: VB_TEXT }),
    T("vb-date", 612 - VB_L - 150, 65, 150, "Date: {saleDate}", 10, { color: VB_TEXT }),

    ...vbHeader("vb-seller-h", 90, "SELLER INFORMATION"),
    vbLabel("vb-s-l1", VB_L, 119, "Full Legal Name:"), vbValue("vb-s-v1", VB_L + 90, 119, "{sellerName}", 400),
    vbLabel("vb-s-l2", VB_L, 133, "Address:"), vbValue("vb-s-v2", VB_L + 90, 133, "{sellerAddress}", 400),
    vbLabel("vb-s-l3", VB_L, 147, "Driver's License / ID:"), vbValue("vb-s-v3", VB_L + 110, 147, "{sellerId}", 160),
    vbLabel("vb-s-l4", VB_L + 280, 147, "State:", 40), vbValue("vb-s-v4", VB_L + 310, 147, "{sellerIdState}", 100),

    ...vbHeader("vb-buyer-h", 172, "BUYER INFORMATION"),
    vbLabel("vb-b-l1", VB_L, 201, "Full Legal Name:"), vbValue("vb-b-v1", VB_L + 90, 201, "{buyerName}", 400),
    vbLabel("vb-b-l2", VB_L, 215, "Address:"), vbValue("vb-b-v2", VB_L + 90, 215, "{buyerAddress}", 400),
    vbLabel("vb-b-l3", VB_L, 229, "Driver's License / ID:"), vbValue("vb-b-v3", VB_L + 110, 229, "{buyerId}", 160),
    vbLabel("vb-b-l4", VB_L + 280, 229, "State:", 40), vbValue("vb-b-v4", VB_L + 310, 229, "{buyerIdState}", 100),

    ...vbHeader("vb-vehicle-h", 254, "VEHICLE INFORMATION"),
    vbLabel("vb-v-l1", VB_L, 283, "Year:", 40), vbValue("vb-v-v1", VB_L + 35, 283, "{vehicleYear}", 60),
    vbLabel("vb-v-l2", VB_L + 100, 283, "Make:", 40), vbValue("vb-v-v2", VB_L + 135, 283, "{vehicleMake}", 140),
    vbLabel("vb-v-l3", VB_L + 280, 283, "Model:", 40), vbValue("vb-v-v3", VB_L + 320, 283, "{vehicleModel}", 190),
    vbLabel("vb-v-l4", VB_L, 297, "VIN:", 30), vbValue("vb-v-v4", VB_L + 30, 297, "{vehicleVin}", 280),
    vbLabel("vb-v-l5", VB_L + 320, 297, "Color:", 40), vbValue("vb-v-v5", VB_L + 355, 297, "{vehicleColor}", 150),
    vbLabel("vb-v-l6", VB_L, 311, "Body Type:", 60), vbValue("vb-v-v6", VB_L + 60, 311, "{vehicleBodyType}", 150),
    vbLabel("vb-v-l7", VB_L + 220, 311, "Odometer Reading:", 100), vbValue("vb-v-v7", VB_L + 320, 311, "{odometerReading} miles", 190),

    ...vbHeader("vb-sale-h", 336, "SALE INFORMATION"),
    vbLabel("vb-sale-l1", VB_L, 365, "Sale Price:", 60), T("vb-sale-v1", VB_L + 65, 365, 150, "{salePrice}", 11, { bold: true, color: VB_TEXT }),
    vbLabel("vb-sale-l2", VB_L + 220, 365, "Payment Method:", 100), vbValue("vb-sale-v2", VB_L + 320, 365, "{paymentMethod}", 190),

    ...vbHeader("vb-odo-h", 385, "ODOMETER DISCLOSURE STATEMENT"),
    T("vb-odo-text", VB_L, 414, VB_W, "The seller certifies that the odometer reading of {odometerReadingShort} miles:", 9, { color: VB_TEXT }),
    ...vbCheck("vb-odo-1", 430, "odometerDisclosure=actual", "Reflects the actual mileage of the vehicle"),
    ...vbCheck("vb-odo-2", 444, "odometerDisclosure=exceeds", "Exceeds the odometer's mechanical limits"),
    ...vbCheck("vb-odo-3", 458, "odometerDisclosure=discrepancy", "Is NOT the actual mileage (discrepancy exists)"),

    ...vbHeader("vb-cond-h", 482, "CONDITION DISCLOSURE"),
    ...vbCheck("vb-cond-1", 511, "conditionType=as-is", "AS-IS: The vehicle is sold \"AS-IS\" without any warranty, express or implied."),
    ...vbCheck("vb-cond-2", 525, "conditionType=warranty", "WITH WARRANTY: The seller provides the following warranty:"),
    // from here down, everything follows the warranty line when it shows
    { id: "vb-cond-end", type: "rect", x: VB_L, y: 539, w: VB_W, h: 0, fill: "none", stroke: "none" },
    vbFlow("vb-warranty", VB_L + 28, VB_W - 28, "Warranty Details: {warrantyDetails}", 9, 11, { after: "vb-cond-end", offset: 3, italic: true, showIf: "hasWarrantyDetails" }),
    vbFlow("vb-cert", VB_L, VB_W - 20, "The undersigned Seller certifies that they are the legal owner of the above-described vehicle and have the right to sell it. The Seller certifies that the vehicle is free of all liens and encumbrances, except as noted. The Buyer certifies they have inspected the vehicle and accepts it in its current condition.", 8, 11, { after: "vb-warranty", offset: 15, spaceAfter: 10 }),
    { id: "vb-sig-h-bar", type: "rect", after: "vb-cert", offset: -2, x: VB_L, w: VB_W, h: 18, fill: VB_PRIMARY, stroke: "none" },
    { id: "vb-sig-h-t", type: "text", after: "vb-sig-h-bar", offset: -4, x: VB_L + 8, w: 400, wrap: true, content: "SIGNATURES", fontSize: 10, lineHeight: 1, bold: true, color: "#ffffff" },
    ...vbSigColumn("s", VB_L),
    ...vbSigColumn("b", VB_SIG_RIGHT),

    // notary acknowledgment, its own page
    { id: "vb-not-bar", type: "rect", page: 2, x: VB_L, y: 70, w: VB_W, h: 18, fill: VB_PRIMARY, stroke: "none", showIf: "includeNotary" },
    T("vb-not-t", VB_L + 8, 84, 400, "NOTARY ACKNOWLEDGMENT (OPTIONAL)", 10, { page: 2, bold: true, color: "#ffffff", showIf: "includeNotary" }),
    T("vb-not-state", VB_L, 104, 170, "State of {notaryState}", 9, { page: 2, color: VB_TEXT, showIf: "includeNotary" }),
    T("vb-not-county", VB_L + 180, 104, 200, "County of {notaryCounty}", 9, { page: 2, color: VB_TEXT, showIf: "includeNotary" }),
    { id: "vb-not-text", type: "text", page: 2, x: VB_L, y: +(122 - 0.85 * 8).toFixed(2), w: VB_W - 20, wrap: true, fontSize: 8, lineHeight: 1.375, color: VB_TEXT, showIf: "includeNotary",
      content: "On this _______ day of _______________, 20____, before me personally appeared the above-named Seller and Buyer, known to me (or proved to me on the basis of satisfactory evidence) to be the persons whose names are subscribed to this instrument, and acknowledged that they executed the same." },
    { id: "vb-not-line", type: "line", page: 2, x: VB_L, y: 191, w: 200, h: 0, color: VB_BORDER, lineWidth: 1, showIf: "includeNotary" },
    T("vb-not-cap", VB_L, 203, 200, "Notary Public Signature", 8, { page: 2, color: VB_TEXT, showIf: "includeNotary" }),
    T("vb-not-exp", VB_L, 223, 260, "My Commission Expires: _______________", 9, { page: 2, color: VB_TEXT, showIf: "includeNotary" }),
    T("vb-not-seal", VB_L + 300, 223, 150, "[NOTARY SEAL]", 9, { page: 2, italic: true, color: VB_TEXT, showIf: "includeNotary" }),
  ],
};

const blank = (v, line) => (v ? String(v) : line);
export function buildVehicleBillOfSaleContext(td) {
  const f = (td && td.formData) || {};
  const addr = (a, c, s, z) => [a, c, s, z].filter(Boolean).join(", ");
  const warranty = f.conditionType === "warranty" ? String(f.warrantyDetails || "").trim() : "";
  return {
    state: blank(f.state, "_______________"), county: blank(f.county, "_______________"),
    saleDate: blank(fmtLongDate(f.saleDate), "_______________"),
    sellerName: blank(f.sellerName, "___________________________"), sellerPrintName: f.sellerName || "",
    sellerAddress: blank(addr(f.sellerAddress, f.sellerCity, f.sellerState, f.sellerZip), "___________________________"),
    sellerId: blank(f.sellerId, "___________________________"), sellerIdState: blank(f.sellerIdState, "____"),
    buyerName: blank(f.buyerName, "___________________________"), buyerPrintName: f.buyerName || "",
    buyerAddress: blank(addr(f.buyerAddress, f.buyerCity, f.buyerState, f.buyerZip), "___________________________"),
    buyerId: blank(f.buyerId, "___________________________"), buyerIdState: blank(f.buyerIdState, "____"),
    vehicleYear: blank(f.vehicleYear, "________"), vehicleMake: blank(f.vehicleMake, "________________"),
    vehicleModel: blank(f.vehicleModel, "________________"), vehicleVin: blank(f.vehicleVin, "________________________________"),
    vehicleColor: blank(f.vehicleColor, "____________"), vehicleBodyType: blank(f.vehicleBodyType, "________________"),
    odometerReading: blank(f.odometerReading, "_________"), odometerReadingShort: blank(f.odometerReading, "_______"),
    salePrice: f.salePrice ? money(f.salePrice) : "$_______________",
    paymentMethod: blank(f.paymentMethod, "________________"),
    odometerDisclosure: f.odometerDisclosure || "", conditionType: f.conditionType || "",
    warrantyDetails: warranty, hasWarrantyDetails: flag(warranty),
    includeNotary: flag(f.includeNotary),
    notaryState: blank(f.notaryState, "_______________"), notaryCounty: blank(f.notaryCounty, "_______________"),
    todayDate: fmtLongDate(todayIso()),
  };
}

export const VEHICLE_BILL_OF_SALE_SAMPLE = {
  formData: {
    template: "classic", primaryColor: "#1a1a4d", accentColor: "#3333aa",
    state: "NY", county: "New York", saleDate: "2026-09-01",
    sellerName: "John Smith", sellerAddress: "123 Main Street",
    sellerCity: "New York", sellerState: "NY", sellerZip: "10001",
    buyerName: "Jane Doe", buyerAddress: "456 Oak Avenue",
    buyerCity: "Brooklyn", buyerState: "NY", buyerZip: "11201",
    vehicleYear: "2019", vehicleMake: "Toyota", vehicleModel: "Camry SE",
    vehicleVin: "4T1B11HK5KU211234", vehicleColor: "Silver", vehicleBodyType: "Sedan",
    odometerReading: "48250", salePrice: "14500", paymentMethod: "Certified Check",
    odometerDisclosure: "actual", conditionType: "as-is",
  },
};

export const VEHICLE_BILL_OF_SALE_TOKEN_GROUPS = [
  { group: "Sale", tokens: [
    ["{state}", "State of sale"], ["{county}", "County"], ["{saleDate}", "Sale date"],
    ["{salePrice}", "Sale price"], ["{paymentMethod}", "Payment method"],
  ] },
  { group: "Seller", tokens: [
    ["{sellerName}", "Seller name"], ["{sellerPrintName}", "Seller name (blank if empty)"], ["{sellerAddress}", "Seller address"],
    ["{sellerId}", "Seller ID"], ["{sellerIdState}", "Seller ID state"],
  ] },
  { group: "Buyer", tokens: [
    ["{buyerName}", "Buyer name"], ["{buyerPrintName}", "Buyer name (blank if empty)"], ["{buyerAddress}", "Buyer address"],
    ["{buyerId}", "Buyer ID"], ["{buyerIdState}", "Buyer ID state"],
  ] },
  { group: "Vehicle", tokens: [
    ["{vehicleYear}", "Year"], ["{vehicleMake}", "Make"], ["{vehicleModel}", "Model"], ["{vehicleVin}", "VIN"],
    ["{vehicleColor}", "Color"], ["{vehicleBodyType}", "Body type"], ["{odometerReading}", "Odometer reading"],
    ["{warrantyDetails}", "Warranty details"],
  ] },
  { group: "Notary", tokens: [
    ["{notaryState}", "Notary state"], ["{notaryCounty}", "Notary county"], ["{todayDate}", "Today's date"],
  ] },
];

export const VEHICLE_BILL_OF_SALE_SHOW_IF = [
  ["", "Always"],
  ["odometerDisclosure=actual", "Odometer: actual mileage"],
  ["odometerDisclosure=exceeds", "Odometer: exceeds limits"],
  ["odometerDisclosure=discrepancy", "Odometer: not actual"],
  ["conditionType=as-is", "Sold as-is"],
  ["conditionType=warranty", "Sold with warranty"],
  ["hasWarrantyDetails", "Has warranty details"],
  ["includeNotary", "Notary block requested"],
];

// Everything layoutEngine needs per document type, keyed by base type.
export const FORM_DOC_TYPES = {
  "cease-and-desist": {
    name: "Cease & Desist", description: "Formal demand letter, Professional style", category: "legal-document",
    layout: DEFAULT_CEASE_AND_DESIST_LAYOUT, buildContext: buildCeaseAndDesistContext,
    sample: CEASE_AND_DESIST_SAMPLE, sampleLabel: "Sample letter",
    tokenGroups: CEASE_AND_DESIST_TOKEN_GROUPS, showIf: CEASE_AND_DESIST_SHOW_IF, tableBindings: [],
  },
  "vehicle-bill-of-sale": {
    name: "Vehicle Bill of Sale", description: "Bill of sale, Classic style with navy border", category: "legal-document",
    layout: DEFAULT_VEHICLE_BILL_OF_SALE_LAYOUT, buildContext: buildVehicleBillOfSaleContext,
    sample: VEHICLE_BILL_OF_SALE_SAMPLE, sampleLabel: "Sample sale",
    tokenGroups: VEHICLE_BILL_OF_SALE_TOKEN_GROUPS, showIf: VEHICLE_BILL_OF_SALE_SHOW_IF, tableBindings: [],
  },
  ...LEGAL_FLOW_DOC_TYPES,
  ...UTILITY_DOC_TYPES,
};
